import { useState } from 'react';
import {
  mensajeDe,
  validarTercero,
  type ContextoDocumento,
  type DatosTercero,
} from '@tanteo/compartido';
import { Boton, Campo, CampoCelular, FilaAcciones, FilaCampos, MensajeError } from './base';
import { CampoDocumento } from './CampoDocumento';

export interface DatosFormularioTercero {
  tipoDocumento: string;
  numeroDocumento: string;
  nombres: string;
  apellidos: string;
  razonSocial: string;
  telefono: string;
  correo: string;
  esCliente: boolean;
  esProveedor: boolean;
  autorizaDatos: boolean;
}

const VACIO: DatosFormularioTercero = {
  tipoDocumento: '13',
  numeroDocumento: '',
  nombres: '',
  apellidos: '',
  razonSocial: '',
  telefono: '',
  correo: '',
  esCliente: true,
  esProveedor: false,
  autorizaDatos: false,
};

interface Props {
  contexto: ContextoDocumento;
  /** Cliente y proveedor a la vez: solo tiene sentido en el CRM, no en el mostrador. */
  mostrarRoles?: boolean;
  ocupado?: boolean;
  errorServidor?: string;
  onGuardar: (datos: DatosFormularioTercero) => void;
  onCancelar?: () => void;
}

/**
 * Alta de una persona u organización. Es el mismo formulario para el mostrador y el CRM:
 * las reglas viven en `@tanteo/compartido` y el servidor las vuelve a aplicar.
 */
export function FormularioTercero({
  contexto,
  mostrarRoles = false,
  ocupado = false,
  errorServidor = '',
  onGuardar,
  onCancelar,
}: Props) {
  const [datos, setDatos] = useState<DatosFormularioTercero>(VACIO);
  const [error, setError] = useState('');

  const esOrganizacion = datos.tipoDocumento === '31';
  const poner = (parte: Partial<DatosFormularioTercero>) => setDatos({ ...datos, ...parte });

  function guardar() {
    const validacion: DatosTercero = {
      tipoDocumento: datos.tipoDocumento,
      numeroDocumento: datos.numeroDocumento,
      nombres: datos.nombres,
      apellidos: datos.apellidos,
      razonSocial: datos.razonSocial,
      telefono: datos.telefono,
      correo: datos.correo,
      contexto,
      autorizaDatos: datos.autorizaDatos,
      exigeConsentimiento: true,
    };
    const codigo = validarTercero(validacion);
    if (codigo) {
      setError(mensajeDe(codigo));
      return;
    }
    setError('');
    onGuardar(datos);
  }

  return (
    <div className="space-y-3">
      <CampoDocumento
        contexto={contexto}
        valor={{ tipo: datos.tipoDocumento, numero: datos.numeroDocumento }}
        onCambio={(d) => poner({ tipoDocumento: d.tipo, numeroDocumento: d.numero })}
      />

      {esOrganizacion ? (
        <Campo
          etiqueta="Razón social"
          autoComplete="organization"
          value={datos.razonSocial}
          onChange={(e) => poner({ razonSocial: e.target.value })}
        />
      ) : (
        <FilaCampos>
          <Campo
            etiqueta="Nombres"
            autoComplete="given-name"
            value={datos.nombres}
            onChange={(e) => poner({ nombres: e.target.value })}
          />
          <Campo
            etiqueta="Apellidos"
            autoComplete="family-name"
            value={datos.apellidos}
            onChange={(e) => poner({ apellidos: e.target.value })}
          />
        </FilaCampos>
      )}

      <FilaCampos>
        <CampoCelular
          etiqueta="Celular"
          value={datos.telefono}
          onChange={(e) => poner({ telefono: e.target.value })}
        />
        <Campo
          etiqueta="Correo"
          type="email"
          autoComplete="email"
          value={datos.correo}
          onChange={(e) => poner({ correo: e.target.value })}
        />
      </FilaCampos>

      {mostrarRoles && (
        <div className="flex gap-4 text-contenido font-semibold">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={datos.esCliente} onChange={(e) => poner({ esCliente: e.target.checked })} />
            Cliente
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={datos.esProveedor}
              onChange={(e) => poner({ esProveedor: e.target.checked })}
            />
            Proveedor
          </label>
        </div>
      )}

      <label className="flex items-start gap-2 text-contenido">
        <input
          type="checkbox"
          className="mt-1"
          checked={datos.autorizaDatos}
          onChange={(e) => poner({ autorizaDatos: e.target.checked })}
          data-e2e="tercero-autoriza"
        />
        <span>
          Confirmo que autorizó el tratamiento de sus datos (nombre, documento, celular y correo) para
          identificarlo, asociar sus compras y contactarlo sobre ellas.
        </span>
      </label>

      <MensajeError e2e="tercero-error">{error || errorServidor}</MensajeError>

      <FilaAcciones>
        {onCancelar && (
          <Boton variante="contorno" onClick={onCancelar} disabled={ocupado}>
            Volver
          </Boton>
        )}
        <Boton onClick={guardar} disabled={ocupado} data-e2e="tercero-guardar">
          {ocupado ? 'Guardando…' : 'Guardar'}
        </Boton>
      </FilaAcciones>
    </div>
  );
}
